import {CircuitBoard, FileText, Gift, Heart, Home, type LucideIcon, MoreHorizontalIcon, Search, ShieldCheck, User, Zap} from "lucide-react"
import type { components } from "@/openapi/generated"

type MainCategoryWithCountDto = components["schemas"]["MainCategoryWithCountDto"]
type FieldOfWorkDto = components["schemas"]["FieldOfWorkDto"]

type TranslateFn = (key: string) => string;

export interface MenuItem {
    key: string
    title: string
    href: string
    description?: string
    icon?: LucideIcon
    children?: MenuItem[]
}

export interface AuthenticatedTab {
    key: string
    title: string
    href: string
    icon: LucideIcon
    /** If true, this tab opens a dropdown instead of navigating */
    isDropdown?: boolean
    /** If true, only shown when a feature flag is enabled */
    featureFlag?: string
    /** If true, only shown for admin users */
    adminOnly?: boolean
}

export interface NavigationData {
    desktopNavigation: MenuItem[],
    mobileNavigation: MenuItem[],
}

// Footer data structures based on footer component needs
export interface FooterLink { name: string; href: string }
export interface FooterSection { title: string; links: FooterLink[]; wide?: boolean }

export function getNavigation(t?: TranslateFn): NavigationData {
    const tr = (key: string, fallback: string) => t ? t(key) : fallback;

    const navShop = {
        key: "shop",
        title: tr("shop.title", "Shops"),
        href: "/shops",
        icon: Zap,
        description: tr("shop.description", "Entdecke unsere Partnershops und spende mit jedem Kauf"),
    };
    const navProjects = {
        key: "projects",
        title: tr("projects.title", "Charities"),
        href: "/projects",
        icon: Heart,
        description: tr("projects.description", "Unterstütze gemeinnützig orientierte Organisationen"),
    };
    const navPartner = {
        key: "partner",
        title: tr("partner.title", "Partner werden"),
        href: "/partner",
        icon: CircuitBoard,
        description: tr("partner.description", "Schließe dich uns als Partner an"),
    };
    const navAboutUs = {
        key: "aboutUs",
        title: tr("aboutUs.title", "Über Uns"),
        href: "/about-us/mission",
        icon: FileText,
        description: tr("aboutUs.description", "Lerne uns und unsere Mission kennen"),
        children: [
            {
                key: "aboutUs.howItWorks",
                title: tr("aboutUs.howItWorks.title", "Funktionsweise"),
                href: "/about-us/how-it-works",
                description: tr("aboutUs.howItWorks.description", "Wie sammeln wir spenden?"),
            },
            {
                key: "aboutUs.transparency",
                title: tr("aboutUs.transparency.title", "Transparenz"),
                href: "/about-us/transparency",
                description: tr("aboutUs.transparency.description", "Wir leben Offenheit"),
            },
            {
                key: "aboutUs.team",
                title: tr("aboutUs.team.title", "Unser Team"),
                href: "/about-us/team",
                description: tr("aboutUs.team.description", "Die Menschen, die donista mit Liebe pflegen"),
            },
        ],
    };
    const navHome = {
        key: "home",
        title: tr("home.title", "Home"),
        href: "/home",
        icon: Home,
        description: tr("home.description", "Zur Startseite"),
    };
    const navMore = {
        key: "more",
        title: tr("more.title", "Mehr"),
        href: "#",
        icon: MoreHorizontalIcon,
        description: tr("more.description", "Weitere Optionen"),
        children: [
            {
                key: "more.partner",
                title: tr("more.partner.title", "Partner werden"),
                href: "/partner",
                description: tr("more.partner.description", "Erfahre mehr über das Projekt und wie du mitmachen kannst."),
            }
        ]
    }

    const nav = [
        navShop,
        navProjects,
        navPartner,
        navAboutUs,
    ];

    const mobileNav = [
        navHome,
        navShop,
        navProjects,
        navMore
    ];

    return {
        desktopNavigation: nav,
        mobileNavigation: mobileNav,
    }
}

export function getAuthenticatedTabs(t?: TranslateFn): AuthenticatedTab[] {
    const tr = (key: string, fallback: string) => t ? t(key) : fallback;

    return [
        { key: "home", title: tr("tabs.home", "Home"), href: "/home", icon: Home },
        { key: "search", title: tr("tabs.search", "Suche"), href: "/search", icon: Search },
        { key: "impact", title: tr("tabs.impact", "Impact"), href: "/impact", icon: Heart },
        { key: "vouchers", title: tr("tabs.vouchers", "Gutscheine"), href: "/vouchers", icon: Gift, featureFlag: "voucherEnabled" },
        { key: "profile", title: tr("tabs.profile", "Profil"), href: "/profile", icon: User },
        { key: "admin", title: tr("tabs.admin", "Admin"), href: "/admin", icon: ShieldCheck, adminOnly: true },
    ];
}

// Build footer sections from the existing navigation plus optional extras
export function getFooterNavigation(t?: TranslateFn | FooterSection[], extraSections: FooterSection[] = []): FooterSection[] {
    // Handle legacy call signature: getFooterNavigation(extraSections)
    let tFn: TranslateFn | undefined;
    let extras = extraSections;
    if (Array.isArray(t)) {
        extras = t;
        tFn = undefined;
    } else {
        tFn = t;
    }

    const tr = (key: string, fallback: string) => tFn ? tFn(key) : fallback;

    const { desktopNavigation } = getNavigation(tFn)

    const sectionsFromHeader: FooterSection[] = desktopNavigation.map((item) => {
        const links: FooterLink[] = []
        if (item.children && item.children.length > 0) {
            for (const child of item.children) {
                links.push({ name: child.title, href: child.href })
            }
        } else if (item.href) {
            links.push({ name: item.title, href: item.href })
        }
        if (item.key === "shop") {
            links.push({ name: tr("footer.allShopsAZ", "Alle Shops A–Z"), href: "/shops/all" })
        }
        if (item.key === "projects") {
            links.push({ name: tr("footer.allProjectsAZ", "Alle Projekte A–Z"), href: "/projects/all" })
        }
        if (item.key === "aboutUs") {
            links.push({ name: tr("aboutUs.amazon.title", "Amazon"), href: "/shops/amazon" })
            links.push({ name: tr("aboutUs.releaseNotes.title", "Release Notes"), href: "/release-notes" })
        }
        if (item.key === "partner") {
            links.push({ name: tr("footer.inviteFriends", "Freunde einladen"), href: "/invite-friends" })
            return { title: tr("footer.getInvolved", "Mitmachen"), links }
        }
        return { title: item.title, links }
    })

    return [...sectionsFromHeader, ...extras]
}

// Build a footer section for main categories, linking each to /categories/{id}
export function buildCategoryFooterSection(
    mains: MainCategoryWithCountDto[],
    t?: TranslateFn,
): FooterSection {
    const title = t ? t("footer.categories") : "Kategorien";
    const links: FooterLink[] = [...mains]
        .sort((a, b) => a.name.localeCompare(b.name, "de-DE"))
        .map((cat) => ({
            name: cat.name,
            href: `/categories/${cat.id}`,
        }))
    return { title, links, wide: true }
}

// Build a footer section for fields of work, linking each to /fields-of-work/{id}
export function buildFieldOfWorkFooterSection(
    fields: FieldOfWorkDto[],
    t?: TranslateFn,
): FooterSection {
    const title = t ? t("footer.topicsOfWork") : "Themenbereiche";
    const links: FooterLink[] = [...fields]
        .sort((a, b) => a.title.localeCompare(b.title, "de-DE"))
        .map((fow) => ({
            name: fow.title,
            href: `/fields-of-work/${fow.id}`,
        }))
    return { title, links, wide: true }
}
